import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PartnerSettings } from "@/types/partner-settings";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  partnerSettingsSchema,
  PartnerSettingsFormValues,
} from "../utils/partnerSettingsSchema";

interface PartnerSectionFormProps {
  settings: PartnerSettings;
  isSaving: boolean;
  isSyncing: boolean;
  onSave: (data: PartnerSettingsFormValues) => void;
  onSync: () => void;
}

// Brand names — not translated, these are proper nouns
const providerLabels: Record<string, string> = {
  BOOKING_COM: "Booking.com",
  VIATOR: "Viator",
};

export const PartnerSectionForm: React.FC<PartnerSectionFormProps> = ({
  settings,
  isSaving,
  isSyncing,
  onSave,
  onSync,
}) => {
  const { t } = useTranslation();

  const { handleSubmit, setValue, watch, reset } =
    useForm<PartnerSettingsFormValues>({
      resolver: zodResolver(partnerSettingsSchema),
      defaultValues: {
        enabled: settings.enabled,
        destinationId: settings.destinationId ?? "",
        destinationName: settings.destinationName ?? "",
        destinationType: settings.destinationType ?? "",
        autoSyncEnabled: settings.autoSyncEnabled,
      },
    });

  useEffect(() => {
    reset({
      enabled: settings.enabled,
      destinationId: settings.destinationId ?? "",
      destinationName: settings.destinationName ?? "",
      destinationType: settings.destinationType ?? "",
      autoSyncEnabled: settings.autoSyncEnabled,
    });
  }, [settings, reset]);

  const autoSyncEnabled = watch("autoSyncEnabled");

  const providerLabel = providerLabels[settings.provider] || settings.provider;

  return (
    <form onSubmit={handleSubmit(onSave)} className="flex flex-col gap-6">
      <h3 className="text-lg font-semibold">{providerLabel}</h3>

      <div className="flex flex-col gap-4">
        {/* Enabled toggle — temporarily hidden
        <div className="flex items-center gap-2">
          <Switch
            id={`enabled-${settings.id}`}
            checked={watch("enabled")}
            onCheckedChange={(checked) =>
              setValue("enabled", checked, { shouldDirty: true })
            }
          />
          <Label htmlFor={`enabled-${settings.id}`} className="text-md">
            {t("partners.enabled")}
          </Label>
        </div>
        */}

        {/* Destination search + ID/type display — temporarily hidden */}

        <div className="flex items-center gap-2">
          <Switch
            id={`autoSync-${settings.id}`}
            checked={autoSyncEnabled}
            disabled={!settings.id || !settings.destinationId}
            onCheckedChange={(checked) =>
              setValue("autoSyncEnabled", checked, { shouldDirty: true })
            }
          />
          <Label htmlFor={`autoSync-${settings.id}`} className="text-md">
            {t("partners.autoSyncEnabled")}
          </Label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={isSaving}>
          {isSaving ? t("partners.saving") : t("partners.save")}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!settings.enabled || isSyncing}
          onClick={onSync}
        >
          {isSyncing ? t("partners.syncing") : t("partners.triggerSync")}
        </Button>
      </div>
    </form>
  );
};
